import React, {useState, useMemo} from 'react';
import {
  View,
  StyleSheet,
  Modal,
  FlatList,
  TouchableOpacity,
  TextInput,
} from 'react-native';
import {Text} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import {onboardingColors} from '../theme/onboardingColors';
import {countries} from '../data/countries';

const getFlag = code =>
  String.fromCodePoint(
    ...code
      .toUpperCase()
      .split('')
      .map(c => 127397 + c.charCodeAt()),
  );

const CountryPickerModal = ({visible, onClose, onSelect}) => {
  const {t} = useTranslation();
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return countries;
    const q = search.toLowerCase();
    return countries.filter(
      c =>
        c.name.toLowerCase().includes(q) ||
        c.dialCode.includes(q) ||
        c.code.toLowerCase().includes(q),
    );
  }, [search]);

  const renderItem = ({item}) => (
    <TouchableOpacity
      style={styles.row}
      onPress={() => {
        onSelect(item);
        setSearch('');
        onClose();
      }}>
      <Text style={styles.flag}>{getFlag(item.code)}</Text>
      <Text style={styles.countryName}>
        {item.name} ({item.code})
      </Text>
      <Text style={styles.dialCode}>{item.dialCode}</Text>
    </TouchableOpacity>
  );

  return (
    <Modal visible={visible} animationType="slide" onRequestClose={onClose}>
      <View style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={onClose} style={styles.closeButton}>
            <Icon name="close" size={24} color={onboardingColors.text} />
          </TouchableOpacity>
          <Text style={styles.title}>{t('registration.selectCountry')}</Text>
        </View>

        <View style={styles.searchContainer}>
          <Icon
            name="magnify"
            size={20}
            color={onboardingColors.textSecondary}
          />
          <TextInput
            style={styles.searchInput}
            placeholder={t('registration.searchCountry')}
            placeholderTextColor={onboardingColors.textSecondary}
            value={search}
            onChangeText={setSearch}
            autoCorrect={false}
          />
        </View>

        <FlatList
          data={filtered}
          renderItem={renderItem}
          keyExtractor={item => item.code}
          keyboardShouldPersistTaps="handled"
        />
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: onboardingColors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 50,
    paddingHorizontal: 16,
    paddingBottom: 12,
  },
  closeButton: {
    padding: 4,
    marginRight: 12,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: onboardingColors.text,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 16,
    marginBottom: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: onboardingColors.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: onboardingColors.dot,
  },
  searchInput: {
    flex: 1,
    marginLeft: 8,
    fontSize: 16,
    color: onboardingColors.text,
    padding: 0,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: onboardingColors.dot,
  },
  flag: {
    fontSize: 24,
    marginRight: 12,
  },
  countryName: {
    flex: 1,
    fontSize: 15,
    color: onboardingColors.text,
  },
  dialCode: {
    fontSize: 15,
    color: onboardingColors.textSecondary,
  },
});

export default CountryPickerModal;
